/**
 * 六边形拼图游戏 - 游戏界面入口
 */
import { PuzzleGame } from './game/PuzzleGame';
import { i18n } from './core/I18nManager';
import { ApiClient } from './core/ApiClient';
import { AuthModal } from './ui/AuthModal';

// 全局游戏实例
let game: PuzzleGame | null = null;

/**
 * 从 URL 获取图片 ID
 */
function getPictureIdFromUrl(): string | null {
    const params = new URLSearchParams(window.location.search);
    return params.get('id');
}

/**
 * 初始化游戏
 */
async function initGame(): Promise<void> {
    // 初始化多语言
    await i18n.init();

    // 懒加载登录弹窗：首次调用 show() 时才创建，避免页面加载时闪烁
    (window as any).authModal = {
        show(form: 'login' | 'register' = 'login') {
            const real = new AuthModal();
            (window as any).authModal = real;
            real.show(form);
        }
    };

    // 获取图片 ID
    const pictureId = getPictureIdFromUrl();
    if (!pictureId) {
        console.error('No picture ID provided');
        window.location.href = 'index.html';
        return;
    }

    // 获取拼图容器
    const puzzleContainer = document.getElementById('puzzle-area');
    if (!puzzleContainer) {
        console.error('Puzzle container not found');
        return;
    }

    // 创建游戏实例 (根据 ID 设定难度逻辑可在此扩展)
    game = new PuzzleGame(puzzleContainer, {
        rings: 5, // 默认圈数
        tabSize: 0.1,
        jitter: 0.04,
        seed: Math.floor(Math.random() * 10000),
        imageSize: 1024,
        enableWarp: false,
        enableRotation: true // 默认值，后续从 URL 覆盖
    });

    // 注册调试命令：在控制台输入 debugNumbers() 切换碎片编号显示
    (window as any).debugNumbers = (show?: boolean) => {
        game?.toggleDebugNumbers(show);
    };

    // ... (中间的 UI 事件绑定代码保持不变)

    // 设置事件
    game.setEvents({
        onGameStart: () => {
            console.log('Game started');
        },
        onGameComplete: (time) => {
            console.log(`Game completed! Time: ${time}ms`);
        },
        onError: (message) => {
            console.error('Game error:', message);
        }
    });

    // 设置 UI 元素（设置计时器和主题选择器）
    // 动态创建主题选择器并添加到页面
    const topBar = document.querySelector('.top-bar') as HTMLElement;
    const backButton = document.getElementById('back-button');
    const btnShowImage = document.getElementById('btn-show-image');
    const btnToggleBaseMap = document.getElementById('btn-toggle-basemap');
    const imagePreviewOverlay = document.getElementById('image-preview-overlay');
    const previewImage = document.getElementById('preview-image') as HTMLImageElement;
    
    let themeSelect: HTMLSelectElement | undefined;
    
    if (backButton) {
        backButton.addEventListener('click', () => {
            game?.autoSave();
            window.location.href = 'index.html';
        });
    }

    // 显示图片逻辑
    if (btnShowImage) {
        let isImageVisible = false;
        btnShowImage.addEventListener('click', () => {
            isImageVisible = !isImageVisible;
            game?.toggleGuideImage(isImageVisible);
            
            // 视觉反馈
            if (isImageVisible) {
                btnShowImage.classList.remove('bg-stone-100/50');
                btnShowImage.classList.add('bg-stone-200');
            } else {
                btnShowImage.classList.remove('bg-stone-200');
                btnShowImage.classList.add('bg-stone-100/50');
            }
        });
    }

    // 切换底板逻辑
    if (btnToggleBaseMap) {
        let basemapVisible = true;
        btnToggleBaseMap.addEventListener('click', () => {
            basemapVisible = !basemapVisible;
            game?.setBaseMapVisible(basemapVisible);
            
            // 视觉反馈：切换按钮状态
            if (basemapVisible) {
                btnToggleBaseMap.classList.remove('bg-stone-200');
                btnToggleBaseMap.classList.add('bg-stone-100/50');
            } else {
                btnToggleBaseMap.classList.remove('bg-stone-100/50');
                btnToggleBaseMap.classList.add('bg-stone-200');
            }
        });
    }

    if (topBar) {
        // 寻找右侧操作容器，将主题选择器放入其中
        const rightActions = topBar.querySelector('.absolute.right-6');
        
        // 创建容器
        const themeContainer = document.createElement('div');
        themeContainer.className = 'theme-selector';
        themeContainer.style.display = 'inline-flex';
        themeContainer.style.alignItems = 'center';
        themeContainer.style.background = 'rgba(245, 245, 244, 0.5)'; // bg-stone-100/50
        themeContainer.style.padding = '4px 12px';
        themeContainer.style.borderRadius = '9999px'; // rounded-full
        themeContainer.style.border = '1px solid rgba(231, 229, 228, 1)'; // border-stone-200

        const label = document.createElement('span');
        label.textContent = i18n.t('theme_label') + ': ';
        label.style.color = 'var(--zen-ink-mid)';
        label.style.fontSize = '12px';
        label.style.fontWeight = '500';
        label.style.marginRight = '4px';

        themeSelect = document.createElement('select');
        themeSelect.style.backgroundColor = 'transparent';
        themeSelect.style.border = 'none';
        themeSelect.style.color = 'var(--zen-ink-deep)';
        themeSelect.style.cursor = 'pointer';
        themeSelect.style.fontSize = '12px';
        themeSelect.style.outline = 'none';
        themeSelect.style.fontFamily = 'var(--font-stack-main)';

        const themes = [
            { value: 'ZEN_LIGHT', label: i18n.t('paper') },
            { value: 'ZEN_DARK', label: i18n.t('phantom') },
            { value: 'ZEN_TEA', label: i18n.t('deep_sea') }
        ];

        themes.forEach(t => {
            const option = document.createElement('option');
            option.value = t.value;
            option.textContent = t.label;
            themeSelect!.appendChild(option);
        });

        themeContainer.appendChild(label);
        themeContainer.appendChild(themeSelect);
        
        if (rightActions) {
            rightActions.prepend(themeContainer);
        } else {
            topBar.appendChild(themeContainer);
        }
    }

    game.setUIElements({
        timerDisplay: document.getElementById('timer-display') || undefined,
        themeSelect: themeSelect
    });

    // 初始化排行榜小部件
    const lbContainer = document.getElementById('leaderboard-container');
    if (lbContainer) {
        game.setLeaderboardContainer(lbContainer);
    }

    // 初始化渲染器
    await game.initialize();

    try {
        // --- 核心重构：根据 ID 从数据库获取元数据 ---
        const puzzleData = await ApiClient.get<any>(`/api/puzzle/${pictureId}`);
        
        // 获取 URL 上的难度设定
        const params = new URLSearchParams(window.location.search);
        const difficulty = params.get('difficulty') || 'easy';
        const enableRotation = params.get('rotation') !== '0';

        // 根据难度更新 Rings (难度系数)
        // 六边形总行数 = Rings * 2 + 1
        let rings = 2; // 默认 easy (5行)
        if (difficulty === 'medium') rings = 3; // 7行
        if (difficulty === 'hard') rings = 4;   // 9行

        game.updateConfig({ rings, enableRotation });

        // 使用数据库回传的 image_url 启动游戏 (来自 R2 或 assets 路径)
        await game.startGame(puzzleData.image_url, pictureId, puzzleData.puzzle_name);
        
    } catch (error) {
        console.error('Failed to load puzzle metadata:', error);
        alert(i18n.t('msg_load_failed'));
        window.location.href = 'index.html';
    }
}

// 页面加载完成后初始化
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initGame);
} else {
    initGame();
}

// 页面关闭前自动保存
window.addEventListener('beforeunload', () => {
    game?.autoSave();
});

// 导出供调试使用
(window as any).puzzleGame = game;
